import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLightbulb, faCircleChevronRight, faCircleChevronLeft,faCheck, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { faAnglesLeft } from '@fortawesome/pro-solid-svg-icons'
// import { GoogleReCaptcha } from 'react-google-recaptcha-v3'

import { Form, Input, Select, Modal, Spin } from 'antd'

// components
import ButtonPrimary from 'components/ButtonPrimary'

import logo from '../../assets/images/logo_key_blue.svg'

// utils
import api from 'lib/api'
import states from 'lib/states'

// styles
import styles from './styles.module.scss'

const DefendantRegister = () => {
  const [step, setStep] = useState(1)
  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingCounties, setIsLoadingCounties] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedCounty, setSelectedCounty] = useState(null)
  const [counties, setCounties] = useState(null)
  const [bondsmanOffices, setBondsmanOffices] = useState(null)
  const [selectedOffice, setSelectedOffice] = useState(null)
  const [magicLink, setMagicLink] = useState(null)

  // sidedoor check.
  // if the sidedoor item in local storage is 
  // populated, the UI will automatically change.
  useEffect(()=> {
    const _fetchBondsmanInfo = async (info) => {
      try {
        const { bondsman } = await api.bondsman.fetchBondsmanByName(info.slug, false)
        let bondsmanStates = []
        let counties = []
        if (counties) {
          bondsmanStates = [...new Set(bondsman.locations.map(itm => itm.state))]
          counties = [...new Set(bondsman.locations.map(itm => itm.name))]
        }

        // If the Agency only serves one state, set that which will trigger the look up on counties...
        if (bondsmanStates.length === 1) {
          const foundState = Object.keys(states).find(key => {
            const keyState = states[key].toUpperCase().replace(/ /g, '_')
            if (keyState === bondsmanStates[0]) {
              return true
            }
            return false
          })
          setData(d => ({ ...d, state: states[foundState] }))
        }
        setMagicLink({ ...info, states: bondsmanStates, counties })
      } catch(e) {
        console.error(e)
      }
    }
    const info = localStorage.getItem('magicLinkAgency')
    if (info) {
      let jsonInfo = JSON.parse(info)
      if (jsonInfo.id) {
        _fetchBondsmanInfo(jsonInfo)
      }
    }
  }, [])

  // triggers when [data] is updated. 
  // this checks when the form is ready to hit the county API and Bond Office API
  useEffect(()=> {
    if (data?.state) { 
      _fetchCounties(data?.state)
    }
    if (data?.county && !bondsmanOffices && !magicLink) { 
      _fetchBondOffices(selectedCounty) 
    }
  },[data]) // eslint-disable-line

  // creates case
  const _createCase = async (data) => {
    setIsLoading(true)

    let entryType = 'FRONT_DOOR'
    let bondsmanId = selectedOffice?.bondsmanId
    if(magicLink?.id) {
      bondsmanId = magicLink.id
      entryType = 'SIDE_DOOR'
    }

    if (data && bondsmanId) {
      const payload = {
        defendantInfo: {
          firstName: data.defNameFirst,
          lastName: data.defNameLast
        },
        seekerInfo: {
          firstName: data.nameFirst,
          lastName: data.nameLast,
          email: data.email,
          phone: data.phone
        },
        bondsman: {
          id: bondsmanId
        },
        countyId: data.county,
        entryType
      }

      try {
        const res = await api.arrestee.createCase(payload)
        window.location = `u/${res?.case?.indemnitors[0]?.token}`
      } catch(e) {
        console.error('Could not post:', e)
      }
    }
    setIsLoading(false)
  }

  // fetches bondsman based off county
  const _fetchBondOffices = async (selectedCounty) => {
    try {
      const res = await api.arrestee.fetchBondsmanByCounty(selectedCounty)
      setBondsmanOffices(res)
    } catch(e) {
      console.error('Could not fetch bond offices: ', e)
    }
  }

  // fetches counties based on state
  const _fetchCounties = async (state) => {
    setIsLoadingCounties(true)
    const slug = state.toLowerCase().replace(/[^A-Za-z0-9\s?]/g,'_').replace(/ /g, '_')
    try {
      const res = await api.counties.fetchCounties(slug)
      setCounties(res.counties)
    } catch(e) {
      console.error('Could not fetch counties: ', e)
    }
    setIsLoadingCounties(false)
  }

  const _getStatesOptions = () => {
    return Object.keys(states).filter(key => {
      if (magicLink?.states) {
        return magicLink.states.includes(states[key].toUpperCase().replace(/ /g, '_'))
      }
      return true
    }).map(key => {
      return { value: key, label: states[key] }
    })
  }

  const _getCountyOptions = () => {
    return counties?.filter(county => {
      if (magicLink?.counties) {
        return magicLink.counties.includes(county.name)
      }
      return true
    }).map(county => {
      return { value: county.name.toUpperCase(), label: county.name, id: county.id }
    })
  }

  // this is the main form that collects the user info.
  const _initialForm = () => {

    const stateOptions = _getStatesOptions()
    const countyOptions = _getCountyOptions()

    return (
      <div className={styles.flowContainer__form}>
        <Spin spinning={isLoading} delay={500}>
          <Form
            name="layout-multiple-horizontal"
            layout="vertical"
            initialValues={data ? data : {}}
            onFinish={(values)=> {
              const newValues = { ...values, ...selectedCounty }
              setData(newValues)
              if(!magicLink) {
                setStep(2)
              } else {
                _createCase(newValues)
              }
            }}
          >
            <>
            {step === 1 && (
              <div className={styles.flowContainer__form_container}>
                <h5>Your Information: </h5>
                <div className={styles.flowContainer__form_row}>
                  <Form.Item layout="vertical" label="First Name" name="nameFirst" rules={[{ required: true }]} >
                    <Input placeholder='John' />
                  </Form.Item>
                  <Form.Item layout="vertical" label="Last Name" name="nameLast" rules={[{ required: true }]}>
                    <Input placeholder='Doe'/>
                  </Form.Item>
                </div>
                <div className={styles.flowContainer__form_row}>
                  <Form.Item layout="vertical" label="Email" name="email" rules={[{ required: true }]} >
                    <Input addonBefore="@" placeholder='me@gmail.com'/>
                  </Form.Item>
                  <Form.Item layout="vertical" label="Phone" name="phone" rules={[{ required: true }]} >
                    <Input addonBefore="#" placeholder='###-###-####'/>
                  </Form.Item>
                </div>
                <div className={styles.flowContainer__form_divider}></div>
                <h5>Defendant Information: </h5>
                <div className={styles.flowContainer__form_row}>
                  <Form.Item layout="vertical" label="Defendant First Name" name="defNameFirst" rules={[{ required: true }]} >
                    <Input placeholder='Jane'/>
                  </Form.Item>
                  <Form.Item layout="vertical" label="Defendant Last Name" name="defNameLast" rules={[{ required: true }]}>
                    <Input placeholder='Doe'/>
                  </Form.Item>
                </div>
                {(stateOptions.length === 1) && (
                  <div className={styles.stateContainer}>
                    <label>State:</label>
                    <p>{stateOptions[0].label}</p>
                  </div>
                )}
                {(stateOptions.length > 1) && (
                  <div className={styles.flowContainer__form_row}>
                    <Form.Item layout="vertical" label="State of Arrest" name="state" rules={[{ required: true }]} block={true}>
                      <Select
                        id="state" 
                        showSearch
                        allowClear
                        loading={isLoadingCounties}
                        optionFilterProp="children"
                        filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
                        filterSort={(optionA, optionB) =>
                          (optionA?.label?.toLowerCase() ?? '').toLowerCase().localeCompare((optionB?.label?.toLowerCase() ?? '').toLowerCase())
                        }
                        style={{ width: '100%' }}
                        options={stateOptions}
                        onChange={(e, value)=> { setData({ ...data, state: value.label, stateShort: e })} }
                        placeholder='Choose a State'
                      />
                    </Form.Item>
                  </div>
                )}
                <div className={styles.flowContainer__form_row}>
                  <Form.Item layout="vertical" label="Select County" name="county" rules={[{ required: true }]} block={true}>
                    <Select
                      id="county" 
                      showSearch
                      allowClear
                      disabled={!counties?.length > 0}
                      optionFilterProp="children"
                      filterOption={(input, option) => (option?.label.toLowerCase() ?? '').includes(input.toLowerCase())}
                      filterSort={(optionA, optionB) =>
                        (optionA?.label?.toLowerCase() ?? '').toLowerCase().localeCompare((optionB?.label?.toLowerCase() ?? '').toLowerCase())
                      }
                      style={{ width: '100%' }}
                      options={countyOptions}
                      onChange={(e, value)=> {
                        _fetchBondOffices(value.id)
                        setSelectedCounty({ county: value.id, countyName: e }) 
                      }}
                      placeholder='Choose a County'
                    />
                  </Form.Item>
                </div>
              </div>
            )}
            {step === 2 && (
              <div className={styles.flowContainer__form_container}>
                <div className={styles.flowContainer__form_row}>
                  test
                </div>
              </div>
            )}
            <div className={styles.flowContainer__form_actions}>
              {step > 1 && <ButtonPrimary fullWidth color='grey' icon={faCircleChevronLeft} onClick={()=> setStep(step - 1)}>Back</ButtonPrimary>}
              <ButtonPrimary 
                type="submit" 
                fullWidth 
                color='teal' 
                icon={faCircleChevronRight}
              >
                {magicLink ? 'Start Case' : 'Continue'}
              </ButtonPrimary>
            </div>
            <div className={styles.consent}>*By clicking "{magicLink ? 'Start Case' : 'Continue'}" you consent to receiving texts and emails from Simply Bail regarding your case.</div>
            </>
          </Form>
        </Spin>
      </div>
    )
  }

  // a simple modal explaining the process.
  const _infoModal = () => {
    return(
      <Modal title="How Simlpy Bail Works" open={isModalOpen} footer={false} onCancel={()=> setIsModalOpen(false)}>
        <div className={styles.modal}>
          <div className={styles.modal_left}>
            <p><strong>1.</strong> You answer a few questions right here, and then we locate your friend or loved one who has been arrested. </p>
            <p><strong>2.</strong> If they are eligible to make bail and be released, we ask you to select a bail bond agent from a list of licensed agents. </p>
            <p><strong>3.</strong> Once the bail bond agent approves your request your payment will be securely processed and the bail bond agent will arrange your friend or loved one's release. </p>
          </div>
          <ButtonPrimary small color='grey' onClick={()=> setIsModalOpen(false)}>Close</ButtonPrimary>
        </div>
      </Modal>
    )
  }

  // upon selecting county, this component will display
  // on tab 2 and list the bondsman available to them.
  const _bondsmanList = () => {

    const offices = bondsmanOffices?.map((itm, key) => {
      let style = styles.flowContainer__agency
      if(key === selectedOffice?.key) style = styles.flowContainer__agencySelected

      return (
        <div className={style} key={key} onClick={()=> setSelectedOffice({ bondsmanId: itm.id, key: key})}>
          <div className={styles.flowContainer__agency_head}>
            <h3>{itm.companyName}</h3>
          </div>
          <div className={styles.flowContainer__agency_body}>
            <p>{itm.companyAddress + ", " + itm.companyLocation + ", " + itm.companyZip}</p>
            <ButtonPrimary 
              small 
              icon={key === selectedOffice?.key ? faCheck : null} 
              color={key === selectedOffice?.key ? 'teal' : 'grey'}
            >
              {key === selectedOffice?.key ? 'Selected' : 'Select Agency'}
            </ButtonPrimary>
          </div>
        </div>
      )

    })

    return (
      <div className={styles.flowContainer__bondsmanSelect_container}>
        {offices?.length > 0 && (
          <div>
            <h3>Select A Bondsman In Your Area</h3>
            <p>These are Bond Agencies In Your Area.</p>
          </div>
        )}
        <div className={styles.offices}>
          {offices}
        </div> 
        {offices.length === 0 && (
          <div className={styles.flowContainer__bondsmanSelect_null}>
            Unfortunetly, we don't have any Bond Agents near your location that can help.
            <ButtonPrimary color='navy' onClick={()=> setStep(1)} icon={faAnglesLeft}>Go Back</ButtonPrimary>
          </div>
        )}
      </div>
    )

  }

  // this renders the info on tab 2 for bond seekers to see.
  const _caseInfo = () => {

    return (
      <div className={styles.flowContainer__info}>
        <div>
          <h3>Your Info <FontAwesomeIcon icon={faCheckCircle} /></h3>
          <div className={styles.flowContainer__info_indem}>
            <h4>{data?.nameFirst + " " + data?.nameLast} <span>Indemnitor</span></h4>
          </div>
        </div>
        <div>
          <h3>Defendant Info <FontAwesomeIcon icon={faCheckCircle} /></h3>
          <div className={styles.flowContainer__info_def}>
            <h4>{data?.defNameFirst + " " + data?.defNameLast} <span>Defendant</span></h4>
          </div>
        </div>
      </div>
    )

  }

  // const _recaptchaCallback = resp => {
  //   if(resp && !data?.recaptchaValue) setData({...data,  recaptchaValue: resp })
  // }

  return (
    <div className={styles.flowContainer}>
      <div className={styles.flowContainer__content}>
        {step !== 2 && (
          <>
            <div className={styles.flowContainer__content_left}>
              <img src={logo} alt="logo"/>
              {magicLink && (
                <div>
                  <p>Your Bond Agents: <br/><strong>{magicLink?.companyName}</strong></p>
                </div>
              )}
              <div>
                <h3>Let's Get Started with the Bail Process.</h3>
                <p>Begin the process of bailing out your friend or loved one by filling out our initial screening form.</p>
              </div>
              <div className={styles.flowContainer__content_left_pod}>
                <h5>How Does Simply Bail Work?</h5>
                <p>Learn more about the bail process.</p>
                <ButtonPrimary small color='white' icon={faLightbulb} onClick={()=> setIsModalOpen(true)}>Learn More</ButtonPrimary>
              </div>
            </div>
            <div className={styles.flowContainer__content_right}>
              {_initialForm()}
            </div>
          </>
        )}
        {step === 2 && (
          <Spin spinning={isLoading} delay={500}>
            <div className={styles.flowContainer__bondsmanSelect}>
              <p>You're almost done... </p>
              {_caseInfo()}
              {_bondsmanList()}
              {bondsmanOffices.length > 0 && <ButtonPrimary fullWidth color='teal' icon={faCheck} onClick={()=> _createCase({...data, ...selectedCounty})}>Start Case</ButtonPrimary>}
              <h5>*By clicking "Start Case" you consent to receiving texts and emails from Simply Bail.</h5>
            </div>
          </Spin>
        )}
      </div>
      {_infoModal()}
      {/* <GoogleReCaptcha onVerify={_recaptchaCallback} /> */}
    </div>
  )

}

export default DefendantRegister